/** @jsx jsx */
import { jsx } from '@emotion/react';

import { DescriptionPage_ProductVariant as ProductVariant } from '__generated__/graphql-types';

import { useLocation, useParams } from 'js/lib/useRouter';

import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import { useGenerateStartFreeTrialTestEpicImpression } from 'bundles/coursera-plus-common/components/elevateLTV/startFreeTrial/useGenerateStartFreeTrialTestEpicImpression';
import useGenerateGooglePriceTestImpression from 'bundles/payments/utils/useGenerateGooglePriceTestImpression';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import { showPathwaysXDP } from 'bundles/unified-common/utils/showPathways';
import AdminBannerNotConfigurable from 'bundles/unified-description-page-common/components/admin/AdminBannerNotConfigurable';
import ConsumerSpecializationEnrollButtonNotConfigurable from 'bundles/unified-description-page-common/components/enroll/consumer/ConsumerSpecializationEnrollButtonNotConfigurable';
import MobileCtaModule from 'bundles/unified-description-page-common/components/modules/mobile-cta/MobileCtaModule';
import FixedPageNavigation from 'bundles/unified-description-page-common/components/modules/page-navigation/FixedPageNavigation';
import NavPartnerLogo from 'bundles/unified-description-page-common/components/modules/page-navigation/NavPartnerLogo';
import SpecializationAndProfessionalCertificatePageSEO from 'bundles/unified-description-page-common/components/seo/SpecializationAndProfessionalCertificatePageSEO';
import CoBrandingPartnerLogos from 'bundles/unified-description-page-common/components/shared/CoBrandingPartnerLogos';
import PageDataErrorHandler from 'bundles/unified-description-page-common/components/shared/PageDataErrorHandler';
import { GOOGLE_XDP_SLUG_TO_PROF_CERT } from 'bundles/unified-description-page-common/constants/googleCerts';
import { useSpecializationPageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import useLogRecentlyViewed from 'bundles/unified-description-page-common/hooks/useRecentlyViewed';
import PathwayCitationModuleRenderer from 'bundles/unified-description-page-common/module-renderers/PathwayCitationModuleRenderer';
import ConsumerProfessionalCertificateCitationModuleRenderer from 'bundles/unified-description-page-common/module-renderers/citation/ConsumerProfessionalCertificateCitationModuleRenderer';
import Renderer from 'bundles/unified-description-page-common/pages/consumer/professional-certificate/ConsumerProfessionalCertificatePageRenderer';
import EventingDataProvider from 'bundles/unified-description-page-common/providers/EventingDataProvider';
import LanguageInfoDialogProvider from 'bundles/unified-description-page-common/providers/LanguageInfoDialogProvider';
import PageInfoProvider from 'bundles/unified-description-page-common/providers/PageInfoProvider';
import PageNavigationProvider from 'bundles/unified-description-page-common/providers/PageNavigationProvider';
import StickyPanelProvider from 'bundles/unified-description-page-common/providers/StickyPanelProvider';
import { useXdpSubscriptionsForwardDiscovery } from 'bundles/unified-description-page-common/providers/SubscriptionsForwardDiscoveryDataProvider';
import TranslationsNotificationBannerProvider from 'bundles/unified-description-page-common/providers/consumer/TranslationsNotificationBannerProvider';
import { ModuleNames } from 'bundles/unified-description-page-common/types/moduleConfig';
import type { ModuleName } from 'bundles/unified-description-page-common/types/moduleConfig';
import {
  SPECIALIZATION_NAV_ITEMS,
  getSpecializationPageNavigationLinks,
} from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';
import getPartnersForSpecialization from 'bundles/unified-description-page-common/utils/getPartnersForSpecialization';
import getProductId from 'bundles/unified-description-page-common/utils/getProductId';
import { getConsumerRouteForSpecializationProducts } from 'bundles/unified-description-page-common/utils/getRouteForSpecializationProducts';
import getVariantTitle from 'bundles/unified-description-page-common/utils/getVariantTitle';
import hasCareerOutcomesModule from 'bundles/unified-description-page-common/utils/hasCareerOutcomesModule';
import isSlugDuplicatedGoogleCert from 'bundles/unified-description-page-common/utils/isSlugDuplicatedGoogleCert';
import { addSubscriptionsForwardDiscoveryModulesForS12n } from 'bundles/unified-description-page-common/utils/useSubscriptionFirstExpUtils';
import {
  addXdpAsADiscoveryToolModulesForS12n,
  getIsUserInXdpAsADiscoveryTool,
} from 'bundles/unified-description-page-common/utils/xdpAsADiscoveryToolUtils';

const ConsumerProfessionalCertificatePageRootContent = () => {
  // Prof certs use same data as for specializations.
  const pageData = useSpecializationPageData();
  const { pathname, search } = useLocation();
  const productSlugInUrl = useParams().productSlug;
  const productId = getProductId('specialization', pageData?.id);
  const shouldShowPathwaysXDP = showPathwaysXDP(!!pageData?.isCareerRolePathway);
  const { shouldShowXdpChanges } = useXdpSubscriptionsForwardDiscovery();

  useLogRecentlyViewed({
    name: pageData?.name,
    slug: pageData?.slug,
    partners: pageData?.partners,
    id: productId,
    difficulty: pageData?.difficultyLevel,
    productType: pageData?.productVariant,
  });

  useGenerateGooglePriceTestImpression({
    hasSpecialization: true,
    partners: pageData?.partners,
  });

  useGenerateStartFreeTrialTestEpicImpression({});

  if (!pageData) {
    return null;
  } else if (isSlugDuplicatedGoogleCert(pageData.slug)) {
    // If duplicated Google Cert slug, redirect to canonical source.
    redirectClientOrServer(
      getConsumerRouteForSpecializationProducts(
        ProductVariant.ProfessionalCertificate,
        GOOGLE_XDP_SLUG_TO_PROF_CERT[pageData.slug as keyof typeof GOOGLE_XDP_SLUG_TO_PROF_CERT]
      )
    );
  } else if (pageData.productVariant === ProductVariant.GoogleCertificate) {
    return <PageDataErrorHandler pathname={pathname} />;
  } else if (
    (pageData.productVariant !== ProductVariant.ProfessionalCertificate &&
      pageData.productVariant !== ProductVariant.ExternalCertificate) ||
    productSlugInUrl !== pageData.slug
  ) {
    redirectClientOrServer(getConsumerRouteForSpecializationProducts(pageData.productVariant, pageData.slug, search));
  }

  const partnersToDisplay = getPartnersForSpecialization(pageData.partners, !!pageData.coBrandingEnabled);
  const productTitle = getProductTitle(pageData.name, pageData.productVariant, pageData.slug);

  const pageConfig = {
    modules: [
      { name: ModuleNames.Breadcrumbs },
      { name: ModuleNames.PageBanner },
      {
        name: ModuleNames.ProductOverview,
      },
      { name: ModuleNames.ThinBanner },
      {
        name: ModuleNames.CareerOutcomes,
      },
      {
        name: ModuleNames.CareerCertificate,
      },
      {
        name: ModuleNames.LearnerIncentive,
      },
      {
        name: ModuleNames.Syllabus,
      },
      {
        name: ModuleNames.EligibleDegrees,
      },
      {
        name: ModuleNames.Testimonials,
      },
      {
        name: ModuleNames.ArticlesRecommendations,
      },
      {
        name: ModuleNames.Upsell,
      },
      {
        name: ModuleNames.Faqs,
      },
    ] as Array<{ name: ModuleName }>,
  };

  const inXdpAsADiscoveryToolExperiment = getIsUserInXdpAsADiscoveryTool();

  if (inXdpAsADiscoveryToolExperiment) {
    pageConfig.modules = addXdpAsADiscoveryToolModulesForS12n(pageConfig.modules);
  }

  if (shouldShowXdpChanges) {
    pageConfig.modules = addSubscriptionsForwardDiscoveryModulesForS12n(pageConfig.modules);
  }

  // impress epic only on XDP that has AI dubbing
  const shouldShowAiDubbingOnXdp = !!pageData.dubbedLanguages?.length;

  const navigationLinks = getSpecializationPageNavigationLinks({
    pageData,
    pageConfig,
    overrides: inXdpAsADiscoveryToolExperiment || shouldShowXdpChanges ? { showRecommendations: true } : undefined,
  });

  return (
    <PageInfoProvider
      navigationIdMap={SPECIALIZATION_NAV_ITEMS}
      productSlug={pageData.slug}
      hasDubbedLanguages={shouldShowAiDubbingOnXdp}
    >
      <EventingDataProvider productId={pageData.id} productType="s12n">
        <PageNavigationProvider navigationLinks={navigationLinks}>
          <StickyPanelProvider>
            <LanguageInfoDialogProvider
              languages={{
                primaryLanguages: pageData.primaryLanguages,
                subtitleLanguages: pageData.subtitleLanguages ?? [],
                translatedLanguages: pageData.translatedLanguages ?? [],
                dubbedLanguages: shouldShowAiDubbingOnXdp ? pageData.dubbedLanguages : null,
              }}
            >
              <TranslationsNotificationBannerProvider
                translatedLanguages={pageData.translatedLanguages}
                productType={getVariantTitle(pageData.productVariant)}
                originalLanguage={pageData.primaryLanguages[0]}
              >
                <AdminBannerNotConfigurable slug={pageData.slug} />
                <FixedPageNavigation
                  productTitle={productTitle}
                  navigationLinks={navigationLinks}
                  pageCta={
                    <ConsumerSpecializationEnrollButtonNotConfigurable
                      pageData={pageData}
                      size="small"
                      onlyShowButton
                    />
                  }
                  partnerLogo={
                    partnersToDisplay && partnersToDisplay.length > 1 ? (
                      <CoBrandingPartnerLogos partners={partnersToDisplay} isNavPlacement={true} />
                    ) : (
                      <NavPartnerLogo
                        // @ts-expect-error URL: string
                        imageSrc={partnersToDisplay?.[0].squareLogo}
                        partnerName={partnersToDisplay?.[0].name}
                      />
                    )
                  }
                />
                <Renderer pageData={pageData} pageConfig={pageConfig} />
                {shouldShowPathwaysXDP && <PathwayCitationModuleRenderer pageData={pageData} />}
                {!shouldShowPathwaysXDP && hasCareerOutcomesModule(pageConfig) && (
                  <ConsumerProfessionalCertificateCitationModuleRenderer pageData={pageData} />
                )}
                <MobileCtaModule
                  pageCta={
                    <ConsumerSpecializationEnrollButtonNotConfigurable
                      pageData={pageData}
                      size="small"
                      onlyShowButton
                    />
                  }
                />
                <SpecializationAndProfessionalCertificatePageSEO pageData={pageData} enableCrawlerIndexing />
              </TranslationsNotificationBannerProvider>
            </LanguageInfoDialogProvider>
          </StickyPanelProvider>
        </PageNavigationProvider>
      </EventingDataProvider>
    </PageInfoProvider>
  );
};

export default ConsumerProfessionalCertificatePageRootContent;
